// The Invoices tab table (CHO-761): every ingested invoice, one row each,
// sorted by due date ascending. Rows without a due date never interleave with
// dated rows — they render after all of them under an explicit "No due date"
// divider, sorted by invoice date descending (the secondary order also breaks
// ties between equal due dates).
//
// Prop-driven: no fetching. Overdue is derived from `today` (injectable for
// tests, defaults to now). Excluded rows are rendered (the page decides
// whether to pass them) but are always marked and never count as overdue.

import type { InvoiceListItem, InvoicePaymentStatus } from '../../types/invoices';
import { formatCurrency } from '../../lib/format';
import { formatInvoiceDate } from '../../lib/payments';
import { isOverdue, localDateISO, propertyLabel, sortInvoices } from '../../lib/invoices';

interface InvoicesTableProps {
  invoices: InvoiceListItem[];
  /** Reference "now" for the overdue rule — tests pin this; defaults to today. */
  today?: Date;
}

const NO_DUE_DATE_DIVIDER = 'No due date';

function billerLabel(invoice: InvoiceListItem): string {
  // Server resolves the display label; fall back to the raw sender domain,
  // and render nothing when both are absent.
  return invoice.biller ?? invoice.senderDomain ?? '';
}

const STATUS_BADGE: Record<InvoicePaymentStatus, { label: string; className: string; title?: string }> = {
  paid: {
    label: 'Paid',
    className: 'bg-green-50 text-green-700 border border-green-200',
  },
  unpaid: {
    label: 'Unpaid',
    className: 'bg-white text-gray-700 border border-gray-300',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700 border border-red-200',
  },
  unknown: {
    // Hollow + dashed so it reads differently from paid/unpaid/failed without
    // colour too; the title states what "unknown" means for reconciliation.
    label: 'Unknown',
    className: 'bg-white text-amber-800 border border-dashed border-amber-400',
    title: 'Attempt outcome unconfirmed — needs reconciliation; money may already have moved',
  },
};

function StatusBadge({ status }: { status: InvoicePaymentStatus }) {
  const badge = STATUS_BADGE[status];
  return (
    <span title={badge.title} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function InvoiceRow({ invoice, todayISO }: { invoice: InvoiceListItem; todayISO: string }) {
  const overdue = isOverdue(invoice, todayISO);
  return (
    <tr className={`border-b border-gray-50 last:border-0 ${invoice.excluded ? 'opacity-50' : ''}`}>
      <td className="px-4 py-3 font-medium text-gray-900">{propertyLabel(invoice)}</td>
      <td className="px-4 py-3 text-gray-600">{billerLabel(invoice)}</td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{invoice.accountIdentifier ?? ''}</td>
      <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">{formatCurrency(invoice.amount, invoice.currency)}</td>
      <td className="px-4 py-3 text-gray-600">{invoice.currency}</td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatInvoiceDate(invoice.invoiceDate)}</td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        {invoice.dueDate ? formatInvoiceDate(invoice.dueDate) : ''}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          <StatusBadge status={invoice.paymentStatus} />
          {overdue && (
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
              Overdue
            </span>
          )}
          {invoice.excluded && (
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200 line-through">
              Excluded
            </span>
          )}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        {invoice.paymentStatus === 'paid' && invoice.paidAt ? formatInvoiceDate(invoice.paidAt) : ''}
      </td>
    </tr>
  );
}

export function InvoicesTable({ invoices, today }: InvoicesTableProps) {
  if (invoices.length === 0) {
    return (
      <p className="text-gray-500 bg-white border border-gray-200 rounded-lg px-4 py-8 text-center">
        No invoices match the current filters.
      </p>
    );
  }

  const todayISO = localDateISO(today ?? new Date());
  const { dated, undated } = sortInvoices(invoices);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th className="px-4 py-3 font-medium">Property</th>
            <th className="px-4 py-3 font-medium">Biller</th>
            <th className="px-4 py-3 font-medium">Account identifier</th>
            <th className="px-4 py-3 font-medium text-right">Amount</th>
            <th className="px-4 py-3 font-medium">Currency</th>
            <th className="px-4 py-3 font-medium">Invoice date</th>
            <th className="px-4 py-3 font-medium">Due date</th>
            <th className="px-4 py-3 font-medium">Payment status</th>
            <th className="px-4 py-3 font-medium">Paid date</th>
          </tr>
        </thead>
        <tbody>
          {dated.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} todayISO={todayISO} />
          ))}
          {undated.length > 0 && (
            <tr className="border-b border-gray-50 bg-gray-50/60" data-testid="no-due-date-divider">
              <td colSpan={9} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {NO_DUE_DATE_DIVIDER}
              </td>
            </tr>
          )}
          {undated.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} todayISO={todayISO} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
